import dayjs from 'dayjs';
import { Dispatch } from 'redux';

// Helper function to split date range into 3-month chunks
export const splitDateRangeInto3Months = (
  startDate: string,
  endDate: string,
): Array<{ start: string; end: string }> => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const chunks: Array<{ start: string; end: string }> = [];

  let current = start.startOf('month');
  while (current.isBefore(end) || current.isSame(end, 'month')) {
    const chunkStart = current.isSame(start, 'month')
      ? start.format('YYYY-MM-DD')
      : current.startOf('month').format('YYYY-MM-DD');

    const chunkEndMonth = current.add(2, 'month');
    let chunkEnd: dayjs.Dayjs;

    if (chunkEndMonth.isAfter(end, 'month')) {
      chunkEnd = end;
    } else if (chunkEndMonth.isSame(end, 'month')) {
      chunkEnd = end;
    } else {
      chunkEnd = chunkEndMonth.endOf('month');
    }

    chunks.push({
      start: chunkStart,
      end: chunkEnd.format('YYYY-MM-DD'),
    });

    current = current.add(3, 'month');
  }

  return chunks;
};

export async function fetchWithDateChunking<TResponse, TMerged>(params: {
  startDate: string;
  endDate: string;
  thresholdMonths: number;
  apiFunction: (dispatch: Dispatch, queryParams: string) => Promise<TResponse>;
  mergeStrategy: (responses: TResponse[]) => TMerged;
  additionalParams?: Record<string, string | number | boolean>;
  dispatch: Dispatch;
}): Promise<TMerged> {
  const {
    startDate,
    endDate,
    thresholdMonths,
    apiFunction,
    mergeStrategy,
    additionalParams = {},
    dispatch,
  } = params;

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const monthsDiff = end.diff(start, 'month', true);

  // If above threshold, split into chunks and make parallel requests
  if (monthsDiff > thresholdMonths) {
    const threeMonthChunks = splitDateRangeInto3Months(startDate, endDate);

    const additionalParamsString = Object.entries(additionalParams)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');

    // Make parallel requests for each 3-month chunk
    const requests = threeMonthChunks.map((chunk) => {
      const queryParams = `startDate=${chunk.start}&endDate=${chunk.end}${
        additionalParamsString ? `&${additionalParamsString}` : ''
      }`;
      return apiFunction(dispatch, queryParams);
    });

    const responses = await Promise.all(requests);

    // Apply merge strategy to combine all responses
    return mergeStrategy(responses);
  } else {
    // Single request for ranges within threshold
    const additionalParamsString = Object.entries(additionalParams)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    const queryParams = `startDate=${startDate}&endDate=${endDate}${
      additionalParamsString ? `&${additionalParamsString}` : ''
    }`;
    const response = await apiFunction(dispatch, queryParams);

    // For single response, wrap it in an array and apply merge strategy
    // This ensures consistent return type
    return mergeStrategy([response]);
  }
}
